import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Drawer,
  InputNumber,
  Select,
  Spin,
  Switch,
  Tag,
  Typography,
} from "antd";
import {
  Box as Bed,
  Calendar,
  CheckCircle,
  DollarSign,
  Globe,
  Lock,
  X,
} from "react-feather";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import "react-datepicker/dist/react-datepicker.css";
import "../distributionPreview.css";

const { Text } = Typography;

const DOW = [
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
  { key: 0, label: "Sun" },
];

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return `${code || ""} `;
};

function countMatchingDates(startDate, endDate, weekdays) {
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).startOf("day");
  if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) return 0;
  const allowed = new Set((weekdays || []).map(Number));
  let count = 0;
  let cursor = start;
  while (cursor.isBefore(end, "day") || cursor.isSame(end, "day")) {
    if (!allowed.size || allowed.has(cursor.day())) count += 1;
    cursor = cursor.add(1, "day");
    if (count > 1000) break;
  }
  return count;
}

function BulkRuleRow({ enabled, onEnabled, title, help, children }) {
  return (
    <div className={`dist-bulk-rule-row ${enabled ? "is-enabled" : ""}`}>
      <Checkbox checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
      <div className="dist-bulk-rule-copy">
        <strong>{title}</strong>
        <span>{help}</span>
      </div>
      <div className="dist-bulk-rule-control">{children}</div>
    </div>
  );
}

function SelectiveRateField({
  enabled,
  onEnabled,
  label,
  value,
  onChange,
  currency,
  help,
}) {
  return (
    <div className={`dist-direct-bulk-field ${enabled ? "is-enabled" : ""}`}>
      <Checkbox checked={enabled} onChange={(e) => onEnabled(e.target.checked)} />
      <div>
        <strong>{label}</strong>
        {help ? <span>{help}</span> : null}
      </div>
      <InputNumber
        min={0}
        disabled={!enabled}
        prefix={currencySymbol(currency)}
        value={value}
        onChange={(v) => onChange(v === null ? null : Number(v))}
      />
    </div>
  );
}

export default function DistributionBulkPreviewDrawer({
  open,
  onClose,
  rooms = [],
  currency = "INR",
  savingAvailability = false,
  onLoadDirectPricing,
  onSaveCommercialUpdate,
}) {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(dayjs().add(6, "day").toDate());
  const [days, setDays] = useState(DOW.map((d) => d.key));
  const [roomIds, setRoomIds] = useState([]);

  const [availabilityEnabled, setAvailabilityEnabled] = useState(false);
  const [availability, setAvailability] = useState(0);
  const [minStayEnabled, setMinStayEnabled] = useState(false);
  const [minStay, setMinStay] = useState(1);
  const [stopSellEnabled, setStopSellEnabled] = useState(false);
  const [stopSell, setStopSell] = useState(false);
  const [ctaEnabled, setCtaEnabled] = useState(false);
  const [cta, setCta] = useState(false);
  const [ctdEnabled, setCtdEnabled] = useState(false);
  const [ctd, setCtd] = useState(false);

  const [directLoading, setDirectLoading] = useState(false);
  const [directState, setDirectState] = useState({
    status: "idle",
    rates: [],
    message: null,
  });
  const [baseRateEnabled, setBaseRateEnabled] = useState(false);
  const [baseRate, setBaseRate] = useState(null);
  const [directDraft, setDirectDraft] = useState({});
  const [otaDraft, setOtaDraft] = useState({});

  useEffect(() => {
    if (!open) return;
    const first = rooms?.[0];
    setRoomIds(first ? [first.pms_room_type_id] : []);
    setAvailability(Number(first?.total_rooms || 0));
    setAvailabilityEnabled(false);
    setMinStayEnabled(false);
    setStopSellEnabled(false);
    setCtaEnabled(false);
    setCtdEnabled(false);
    setDirectState({ status: "idle", rates: [], message: null });
    setDirectDraft({});
    setBaseRateEnabled(false);
    setBaseRate(null);
    setOtaDraft({});
  }, [open, rooms]);

  const selectedRooms = useMemo(
    () =>
      rooms.filter((r) =>
        roomIds.some((id) => String(id) === String(r.pms_room_type_id))
      ),
    [rooms, roomIds]
  );

  const matchingDates = useMemo(
    () => countMatchingDates(startDate, endDate, days),
    [startDate, endDate, days]
  );

  const directRoomId = roomIds.length === 1 ? Number(roomIds[0]) : null;

  const selectedPricingRoom =
    roomIds.length === 1
      ? rooms.find(
          (room) =>
            String(room?.pms_room_type_id) === String(roomIds[0])
        ) || null
      : null;

  const otaPlans = useMemo(() => {
    if (!selectedPricingRoom) return [];
    return (selectedPricingRoom?.ratePlans || [])
      .map((plan) => {
        const occupancies =
          Array.isArray(plan?.occupancies) && plan.occupancies.length
            ? plan.occupancies
                .map(Number)
                .filter((n) => Number.isFinite(n) && n > 0)
            : [Number(plan?.occ || plan?.occupancy || 2) || 2];

        return {
          id: String(plan?.id || ""),
          title: plan?.title || "OTA Rate Plan",
          occupancies,
        };
      })
      .filter((plan) => plan.id);
  }, [selectedPricingRoom]);

  useEffect(() => {
    const next = {};
    otaPlans.forEach((plan) => {
      next[plan.id] = {
        rate_plan_id: plan.id,
        rates: Object.fromEntries(
          plan.occupancies.map((occ) => [
            String(occ),
            { enabled: false, value: null },
          ])
        ),
      };
    });
    setOtaDraft(next);
  }, [otaPlans]);


  useEffect(() => {
    let active = true;
    if (!open || !directRoomId || !onLoadDirectPricing) {
      setDirectState({
        status: roomIds.length > 1 ? "multi" : "idle",
        rates: [],
        message:
          roomIds.length > 1
            ? "Select exactly one room type to edit Direct pricing."
            : null,
      });
      setDirectDraft({});
      setBaseRateEnabled(false);
      return () => {
        active = false;
      };
    }

    setDirectLoading(true);
    onLoadDirectPricing({ roomId: directRoomId })
      .then((result) => {
        if (!active) return;
        const rates = result?.rates || [];
        setDirectState(result || { status: "error", rates: [] });

        const nextDraft = {};
        rates.forEach((rate) => {
          const rateId = String(Number(rate.rate_id ?? rate.id));
          nextDraft[rateId] = {
            rate_id: Number(rateId),
            title:
              rate.display_label ||
              rate.rate_name ||
              rate.title ||
              rate.name ||
              rate.meal_plan ||
              "Booking Engine Rate",
            is_guest_wise_rate: Boolean(rate.is_guest_wise_rate),
            occ1_rate: {
              enabled: false,
              value: rate?.occ1_rate == null ? null : Number(rate.occ1_rate),
            },
            occ2_rate: {
              enabled: false,
              value: rate?.occ2_rate == null ? null : Number(rate.occ2_rate),
            },
            guest_wise_rates: Object.fromEntries(
              Object.entries(rate?.guest_wise_rates || {}).map(
                ([guestCount, value]) => [
                  String(guestCount),
                  {
                    enabled: false,
                    value:
                      value == null || value === "" ? null : Number(value),
                  },
                ]
              )
            ),
            currency_code: String(
              rate?.currency_code || currency || "INR"
            ).toUpperCase(),
          };
        });
        setDirectDraft(nextDraft);
        setBaseRateEnabled(false);
        const roomDefaults = result?.roomTypeDefaults || {};
        const defaultRoomPrice = [
          roomDefaults?.base_rate_single,
          roomDefaults?.base_rate_double,
        ]
          .map(Number)
          .find((value) => Number.isFinite(value) && value > 0);
        setBaseRate(defaultRoomPrice ?? null);
      })
      .catch((error) => {
        if (!active) return;
        setDirectState({
          status: "error",
          rates: [],
          message: error?.message || "Direct pricing could not be loaded.",
        });
      })
      .finally(() => {
        if (active) setDirectLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, directRoomId, roomIds.length, onLoadDirectPricing, currency]);

  const globalEnabledCount = [
    availabilityEnabled,
    minStayEnabled,
    stopSellEnabled,
    ctaEnabled,
    ctdEnabled,
  ].filter(Boolean).length;

  const directEnabledCount = useMemo(() => {
    let count = baseRateEnabled ? 1 : 0;
    Object.values(directDraft || {}).forEach((rate) => {
      if (rate?.occ1_rate?.enabled) count += 1;
      if (rate?.occ2_rate?.enabled) count += 1;
      Object.values(rate?.guest_wise_rates || {}).forEach((guest) => {
        if (guest?.enabled) count += 1;
      });
    });
    return count;
  }, [directDraft, baseRateEnabled]);

  const directValuesValid = useMemo(() => {
    if (baseRateEnabled) {
      if (baseRate === null || !Number.isFinite(Number(baseRate)) || Number(baseRate) < 0) {
        return false;
      }
    }

    for (const rate of Object.values(directDraft || {})) {
      if (rate?.occ1_rate?.enabled) {
        const value = Number(rate.occ1_rate.value);
        if (!Number.isFinite(value) || value < 0) return false;
      }
      if (rate?.occ2_rate?.enabled) {
        const value = Number(rate.occ2_rate.value);
        if (!Number.isFinite(value) || value < 0) return false;
      }
      for (const guest of Object.values(rate?.guest_wise_rates || {})) {
        if (!guest?.enabled) continue;
        const value = Number(guest.value);
        if (!Number.isFinite(value) || value < 0) return false;
      }
    }

    return true;
  }, [directDraft, baseRateEnabled, baseRate]);

  const otaEnabledCount = useMemo(() => {
    let count = 0;
    Object.values(otaDraft || {}).forEach((plan) => {
      Object.values(plan?.rates || {}).forEach((rate) => {
        if (rate?.enabled) count += 1;
      });
    });
    return count;
  }, [otaDraft]);

  const otaValuesValid = useMemo(() => {
    for (const plan of Object.values(otaDraft || {})) {
      for (const config of Object.values(plan?.rates || {})) {
        if (!config?.enabled) continue;
        const value = Number(config.value);
        if (!Number.isFinite(value) || value < 0) return false;
      }
    }
    return true;
  }, [otaDraft]);

  const totalEnabledCount =
    globalEnabledCount + directEnabledCount + otaEnabledCount;

  const toggleDay = (key) =>
    setDays((current) =>
      current.includes(key)
        ? current.filter((d) => d !== key)
        : [...current, key]
    );

  const patchRateField = (rateId, field, patch) => {
    const key = String(rateId);
    setDirectDraft((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        [field]: {
          ...(current[key]?.[field] || {}),
          ...patch,
        },
      },
    }));
  };

  const patchGuestField = (rateId, guestCount, patch) => {
    const key = String(rateId);
    setDirectDraft((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        guest_wise_rates: {
          ...(current[key]?.guest_wise_rates || {}),
          [String(guestCount)]: {
            ...(current[key]?.guest_wise_rates?.[String(guestCount)] || {}),
            ...patch,
          },
        },
      },
    }));
  };

  const patchOtaField = (ratePlanId, occupancy, patch) => {
    const planKey = String(ratePlanId);
    const occKey = String(Number(occupancy));
    setOtaDraft((current) => ({
      ...current,
      [planKey]: {
        ...(current[planKey] || {
          rate_plan_id: planKey,
          rates: {},
        }),
        rates: {
          ...(current[planKey]?.rates || {}),
          [occKey]: {
            ...(current[planKey]?.rates?.[occKey] || {}),
            ...patch,
          },
        },
      },
    }));
  };

  const buildOtaPricingPayload = () => {
    if (!otaEnabledCount) return null;
    const rates = [];

    Object.values(otaDraft || {}).forEach((plan) => {
      Object.entries(plan?.rates || {}).forEach(([occupancy, config]) => {
        if (!config?.enabled) return;
        const amount = Number(config.value);
        if (!Number.isFinite(amount) || amount < 0) return;
        rates.push({
          rate_plan_id: String(plan.rate_plan_id),
          occupancy: Math.max(1, Number(occupancy) || 1),
          amount,
        });
      });
    });

    return rates.length ? { rates } : null;
  };

  const buildDirectPricingPayload = () => {
    if (!directEnabledCount) return null;

    const payload = { rateOverrides: [] };
    if (baseRateEnabled) {
      payload.baseRate = Number(baseRate);
    }

    Object.values(directDraft || {}).forEach((rate) => {
      const patch = {
        rate_id: Number(rate?.rate_id),
        currency_code: String(
          rate?.currency_code || currency || "INR"
        ).toUpperCase(),
      };
      let hasValue = false;

      if (rate?.occ1_rate?.enabled) {
        const value = Number(rate.occ1_rate.value);
        if (Number.isFinite(value) && value >= 0) {
          patch.occ1_rate = value;
          hasValue = true;
        }
      }
      if (rate?.occ2_rate?.enabled) {
        const value = Number(rate.occ2_rate.value);
        if (Number.isFinite(value) && value >= 0) {
          patch.occ2_rate = value;
          hasValue = true;
        }
      }

      const guestPatch = {};
      Object.entries(rate?.guest_wise_rates || {}).forEach(
        ([guestCount, config]) => {
          if (!config?.enabled) return;
          const value = Number(config.value);
          if (!Number.isFinite(value) || value < 0) return;
          guestPatch[String(guestCount)] = value;
          hasValue = true;
        }
      );
      if (Object.keys(guestPatch).length) {
        patch.guest_wise_rates = guestPatch;
      }

      if (hasValue) payload.rateOverrides.push(patch);
    });

    return payload;
  };

  const save = async () => {
    const globalUpdates = {};
    if (availabilityEnabled) {
      globalUpdates.availability = Math.max(0, Number(availability || 0));
    }
    if (minStayEnabled) {
      globalUpdates.min_stay = Math.max(1, Number(minStay || 1));
    }
    if (stopSellEnabled) globalUpdates.stop_sell = Boolean(stopSell);
    if (ctaEnabled) globalUpdates.closed_to_arrival = Boolean(cta);
    if (ctdEnabled) globalUpdates.closed_to_departure = Boolean(ctd);

    const directPricing = buildDirectPricingPayload();
    const otaPricing = buildOtaPricingPayload();

    const result = await onSaveCommercialUpdate?.({
      roomIds,
      startDate,
      endDate,
      weekdays: days,
      globalUpdates,
      directPricing,
      otaPricing,
    });

    if (result?.success) onClose?.();
  };

  const directSingleRoomReady =
    directState?.status === "live" && roomIds.length === 1;
  const hasPlans = (directState?.rates || []).length > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      zIndex={6500}
      width={820}
      closable={false}
      rootClassName="cm-distribution-drawer cm-distribution-bulk-drawer"
      keyboard
      styles={{
        body: { padding: 0, background: "#f8fafc" },
        content: { borderRadius: "18px 0 0 18px", overflow: "hidden" },
      }}
    >
      <div className="dist-drawer-header dist-drawer-header-compact">
        <div>
          <div className="dist-preview-kicker">Bulk Commercial Update</div>
          <h3>Global controls + Direct + OTA pricing</h3>
          <div className="dist-drawer-subtitle">
            One commercial workspace. Update either pricing domain or both in one save.
          </div>
        </div>
        <div className="dist-drawer-head-actions">
          <button type="button" className="dist-close-btn" onClick={onClose} aria-label="Close bulk update">
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="dist-drawer-body">
        <section className="dist-section-card">
          <div className="dist-section-heading">
            <div className="dist-section-icon"><Calendar size={18} /></div>
            <div>
              <div className="dist-step-label">STEP 1</div>
              <h4>Dates & Rooms</h4>
              <p>Choose the exact operational scope.</p>
            </div>
          </div>

          <div className="dist-date-range-preview">
            <label>
              <span>From</span>
              <DatePicker
                selected={startDate}
                onChange={(d) => d && setStartDate(d)}
                minDate={new Date()}
                dateFormat="dd MMM yyyy"
                popperClassName="cm-datepicker-popper"
                popperPlacement="bottom-start"
              />
            </label>
            <label>
              <span>To</span>
              <DatePicker
                selected={endDate}
                onChange={(d) => d && setEndDate(d)}
                minDate={startDate}
                dateFormat="dd MMM yyyy"
                popperClassName="cm-datepicker-popper"
                popperPlacement="bottom-start"
              />
            </label>
          </div>

          <div className="dist-weekday-row">
            {DOW.map((d) => (
              <button
                key={d.key}
                type="button"
                className={days.includes(d.key) ? "active" : ""}
                onClick={() => toggleDay(d.key)}
              >
                {d.label}
              </button>
            ))}
          </div>

          <Select
            mode="multiple"
            value={roomIds}
            onChange={setRoomIds}
            style={{ width: "100%" }}
            placeholder="Select room types"
            options={rooms.map((r) => ({
              value: r.pms_room_type_id,
              label: `${r.name} · ${r.total_rooms || 0} rooms`,
            }))}
            popupClassName="cm-select-popup"
            getPopupContainer={() => document.body}
          />
        </section>

        <section className="dist-section-card dist-selling-card">
          <div className="dist-section-heading">
            <div className="dist-section-icon"><Globe size={18} /></div>
            <div>
              <div className="dist-step-label">STEP 2</div>
              <h4>Global Availability & Selling Rules</h4>
              <p>
                No global field is preselected. Enable only what you intend to change; unchecked fields are preserved.
              </p>
            </div>
            <Tag color="green">LIVE GLOBAL WRITE</Tag>
          </div>

          <div className="dist-applies-everywhere dist-applies-everywhere-top">
            <strong>Applies everywhere</strong>
            <span>Booking Engine</span>
            <span>All OTAs via existing Channex ARI</span>
          </div>

          <div className="dist-bulk-rule-stack">
            <BulkRuleRow
              enabled={availabilityEnabled}
              onEnabled={setAvailabilityEnabled}
              title="Available Rooms"
              help="One room-level inventory number."
            >
              <InputNumber
                min={0}
                disabled={!availabilityEnabled}
                value={availability}
                onChange={(v) => setAvailability(Number(v || 0))}
                addonAfter="rooms"
              />
            </BulkRuleRow>

            <BulkRuleRow
              enabled={minStayEnabled}
              onEnabled={setMinStayEnabled}
              title="Minimum Stay"
              help="One global minimum-stay value for Direct + Channex."
            >
              <InputNumber
                min={1}
                max={30}
                disabled={!minStayEnabled}
                value={minStay}
                onChange={(v) => setMinStay(Math.max(1, Number(v || 1)))}
                addonAfter="nights"
              />
            </BulkRuleRow>

            <BulkRuleRow
              enabled={stopSellEnabled}
              onEnabled={setStopSellEnabled}
              title="Stop Sell"
              help="Close or reopen selected dates everywhere."
            >
              <Switch
                disabled={!stopSellEnabled}
                checked={stopSell}
                onChange={setStopSell}
              />
            </BulkRuleRow>

            <BulkRuleRow
              enabled={ctaEnabled}
              onEnabled={setCtaEnabled}
              title="Closed to Arrival"
              help="Prevent check-in on selected dates."
            >
              <Switch disabled={!ctaEnabled} checked={cta} onChange={setCta} />
            </BulkRuleRow>

            <BulkRuleRow
              enabled={ctdEnabled}
              onEnabled={setCtdEnabled}
              title="Closed to Departure"
              help="Prevent check-out on selected dates."
            >
              <Switch disabled={!ctdEnabled} checked={ctd} onChange={setCtd} />
            </BulkRuleRow>

            <div className="dist-bulk-rule-row is-locked">
              <Lock size={15} />
              <div className="dist-bulk-rule-copy">
                <strong>Maximum Stay</strong>
                <span>
                  Not global yet because Booking Engine Manual Inventory does not
                  expose Max Stay.
                </span>
              </div>
              <Tag>Not global</Tag>
            </div>
          </div>
        </section>

        <section className="dist-section-card dist-pricing-domain-card direct-domain">
          <div className="dist-section-heading">
            <div className="dist-section-icon direct"><Bed size={18} /></div>
            <div>
              <div className="dist-step-label">STEP 3</div>
              <h4>Direct / Booking Engine Pricing</h4>
              <p>
                Apply the same Direct date override across the selected date
                range. Pricing never changes OTA rates.
              </p>
            </div>
            <Tag className="dist-direct-tag">LIVE BE WRITE</Tag>
          </div>

          {roomIds.length !== 1 ? (
            <div className="dist-empty-rate-state">
              Select exactly one room type to edit Direct pricing. Global
              controls can still be applied to multiple room types.
            </div>
          ) : directLoading ? (
            <div className="dist-empty-rate-state">
              <Spin size="small" /> Loading Booking Engine rates…
            </div>
          ) : directState?.status === "unmapped" ||
            directState?.status === "error" ? (
            <div className="dist-empty-rate-state">
              {directState?.message || "Direct pricing is unavailable."}
            </div>
          ) : !hasPlans ? (
            <div className="dist-base-rate-editor">
              <div>
                <strong>No Booking Engine rate plan configured</strong>
                <span>
                  Enable Direct Base Rate to write the existing BE{" "}
                  <code>override_rate</code> across all matching dates.
                </span>
              </div>
              <SelectiveRateField
                enabled={baseRateEnabled}
                onEnabled={setBaseRateEnabled}
                label="Direct Base Rate"
                value={baseRate}
                onChange={setBaseRate}
                currency={currency}
                help="Same date selling rate on every matching date."
              />
            </div>
          ) : (
            <div className="dist-direct-bulk-plans">
              <div className="dist-direct-edit-note">
                <strong>Selective bulk override</strong>
                <span>
                  Tick only the occupancy/guest prices you want to change.
                  Unchecked Direct fields remain untouched on every date.
                </span>
              </div>

              {(directState?.rates || []).map((rate) => {
                const rateId = String(Number(rate.rate_id ?? rate.id));
                const draft = directDraft?.[rateId] || {};
                const title =
                  rate.display_label ||
                  rate.rate_name ||
                  rate.title ||
                  rate.name ||
                  rate.meal_plan ||
                  "Booking Engine Rate";

                return (
                  <div className="dist-direct-bulk-plan" key={rateId}>
                    <div className="dist-rate-plan-title">
                      <div>
                        <strong>{title}</strong>
                        <small>Master values shown as starting point</small>
                      </div>
                      <Tag>Direct</Tag>
                    </div>

                    {rate?.is_guest_wise_rate ? (
                      <div className="dist-direct-bulk-field-stack">
                        {Object.keys(rate?.guest_wise_rates || {})
                          .sort((a, b) => Number(a) - Number(b))
                          .map((guestCount) => (
                            <SelectiveRateField
                              key={guestCount}
                              enabled={
                                Boolean(
                                  draft?.guest_wise_rates?.[guestCount]?.enabled
                                )
                              }
                              onEnabled={(enabled) =>
                                patchGuestField(rateId, guestCount, { enabled })
                              }
                              label={`${guestCount} guest${
                                String(guestCount) === "1" ? "" : "s"
                              }`}
                              value={
                                draft?.guest_wise_rates?.[guestCount]?.value
                              }
                              onChange={(value) =>
                                patchGuestField(rateId, guestCount, { value })
                              }
                              currency={currency}
                              help="Date override"
                            />
                          ))}
                      </div>
                    ) : (
                      <div className="dist-direct-bulk-field-stack">
                        <SelectiveRateField
                          enabled={Boolean(draft?.occ1_rate?.enabled)}
                          onEnabled={(enabled) =>
                            patchRateField(rateId, "occ1_rate", { enabled })
                          }
                          label="Single"
                          value={draft?.occ1_rate?.value}
                          onChange={(value) =>
                            patchRateField(rateId, "occ1_rate", { value })
                          }
                          currency={currency}
                          help="Date override"
                        />
                        <SelectiveRateField
                          enabled={Boolean(draft?.occ2_rate?.enabled)}
                          onEnabled={(enabled) =>
                            patchRateField(rateId, "occ2_rate", { enabled })
                          }
                          label="Double"
                          value={draft?.occ2_rate?.value}
                          onChange={(value) =>
                            patchRateField(rateId, "occ2_rate", { value })
                          }
                          currency={currency}
                          help="Date override"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="dist-section-card dist-pricing-domain-card ota-domain">
          <div className="dist-section-heading">
            <div className="dist-section-icon ota"><DollarSign size={18} /></div>
            <div>
              <div className="dist-step-label">STEP 4</div>
              <h4>OTA Pricing</h4>
              <p>
                Select only the mapped plan + occupancy values you want to
                replace across the chosen dates.
              </p>
            </div>
            <Tag className="dist-ota-tag">LIVE OTA WRITE</Tag>
          </div>

          {roomIds.length !== 1 ? (
            <div className="dist-empty-rate-state">
              Select exactly one room type to edit Direct or OTA pricing.
              Global controls can still target multiple rooms.
            </div>
          ) : !otaPlans.length ? (
            <div className="dist-empty-rate-state">
              No active mapped OTA rate plans were returned for this room.
            </div>
          ) : (
            <div className="dist-direct-bulk-plan-stack">
              {otaPlans.map((plan) => {
                const draft = otaDraft?.[plan.id] || {};
                return (
                  <div className="dist-direct-bulk-plan ota-bulk-plan" key={plan.id}>
                    <div className="dist-rate-plan-title">
                      <div>
                        <strong>{plan.title}</strong>
                        <small>Mapped Channel Manager / Channex plan</small>
                      </div>
                      <Tag>OTA</Tag>
                    </div>

                    <div className="dist-direct-bulk-field-stack">
                      {plan.occupancies.map((occ) => (
                        <SelectiveRateField
                          key={occ}
                          enabled={Boolean(draft?.rates?.[String(occ)]?.enabled)}
                          onEnabled={(enabled) =>
                            patchOtaField(plan.id, occ, { enabled })
                          }
                          label={`×${occ} occupancy`}
                          value={draft?.rates?.[String(occ)]?.value}
                          onChange={(value) =>
                            patchOtaField(plan.id, occ, { value })
                          }
                          currency={currency}
                          help="Apply to selected dates"
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <div className="dist-flow-summary dist-flow-summary-compact">
          <CheckCircle size={17} />
          <div>
            <strong>Review scope</strong>
            <span>
              {selectedRooms.length || 0} room type(s) · {matchingDates} matching
              date(s) · {globalEnabledCount} global field(s) ·{" "}
              {directEnabledCount} Direct price field(s) ·{" "}
              {otaEnabledCount} OTA price field(s).
            </span>
          </div>
        </div>
      </div>

      <div className="dist-drawer-footer">
        <Text type="secondary">
          Global controls fan out everywhere. Direct and OTA prices remain
          separate rate engines, but this one Save Commercial Update action can
          commit both domains together.
        </Text>
        <div>
          <Button onClick={onClose} disabled={savingAvailability}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={savingAvailability}
            disabled={
              !selectedRooms.length ||
              !matchingDates ||
              !totalEnabledCount ||
              ((directEnabledCount > 0 || otaEnabledCount > 0) &&
                roomIds.length !== 1) ||
              !directValuesValid ||
              !otaValuesValid
            }
            onClick={save}
          >
            Save Commercial Update
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
